<script>
import { mapActions } from 'vuex'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'TracksManageSEO',
  components: {
    Action: () => import('@/components/general/Action'),
    ActionBar: () => import('@/components/general/ActionBar'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    InputField: () => import('@/components/general/InputField'),
    Pagination: () => import('@/components/general/Pagination')
  },

  data () {
    return {
      pagination: {
        page: 3,
        lastPage: 4
      },

      formData: {
        id: null,
        name: '',
        callText: '',
        keywords: ''
      }
    }
  },

  validations: {
    formData: {
      name: { required },
      callText: { required },
      keywords: {
        required,
        hasKeywordsFormat (term) {
          return (
            /[a-z,]/.test(term) &&
            !/ /.test(term)
          )
        }
      }
    }
  },

  computed: {
    isEditing () {
      return this.$route.params.id && this.$route.params.isNew !== 'new'
    }
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptSaveTrack',
      'attemptUpdateTrack',
      'attemptUpdateActiveTrack',
      'attemptSaveActiveTrack'
    ]),

    prevPage () {
      this.$v.$touch()

      if (!this.$v.$invalid) {
        this.$router.push({
          name: 'tracks.manage.qualify',
          params: {
            id: 1,
            isNew: 'edit'
          }
        })

        if (this.formData.id != null) {
          const formData = this.formatFormData()

          this.attemptUpdateActiveTrack({
            id: this.$route.params.id,
            data: formData
          }).then(() => {
            this.$router.push({ name: 'tracks.manage.qualify' })
          })
        } else {
          this.attemptSaveActiveTrack(this.formData).then(() => {
            this.$router.push({ name: 'tracks.manage.qualify' })
          })
        }
      }
    },

    nextPage () {
      this.$v.$touch()

      if (!this.$v.$invalid) {
        this.$router.push({ name: 'tracks.manage.link' })
      }
    },

    leave () {
      this.modalConfirm = false

      this.$nextTick(() => {
        this.$router.push(this.backUrl)
      })
    },

    submitCreation () {
      this.setFetching(true)

      this.attemptSaveTrack(this.formData).then(({ data }) => {
        this.reports.items.push(data)
        this.setFeedback({ message: this.$t('reports:feedback.created.success') })
        this.$router.push({ name: 'reports.list' })
      }).catch(({ response }) => {
        this.setFeedback({ message: this.$t(`reports:feedback.created.error:${response.data.message.replace(/_/g, '.')}`) })
        this.$router.push({ name: 'reports.list' })
      }).finally(() => {
        this.setFetching(false)
      })
    },

    submitUpdate () {
      this.setFetching(true)
      const formData = this.formatFormData()

      this.attemptUpdateTrack({
        id: this.$route.params.id,
        data: formData
      }).then(({ data }) => {
        if (data.imagePath) {
          data.imagePath = data.imagePath + '?v=' + Math.random()
        }

        this.setFeedback({ message: this.$t('tracks:feedback.updated.success') })
        this.$router.push({ name: 'tracks.index' })
      }).catch(() => {
        this.setFeedback({ message: this.$t('tracks:feedback.updated.error') })
        this.$router.push({ name: 'tracks.index' })
      }).finally(() => {
        this.setFetching(false)
      })
    },

    submit (next) {
      this.$v.$touch()

      if (!this.$v.$invalid) {
        this.$route.params.id ? this.submitUpdate(next) : this.submitCreation(next)
      } else {
        this.setFeedback({ message: this.$t('tracks:feedback.validation.error') })
      }
    },

    formatFormData () {
      const data = {}

      data.name = this.formData.name
      data.call_text = this.formData.callText
      data.keywords = this.formData.keywords
      return data
    }
  }
}
</script>

<template>
  <div class="main-content tracks-create">
    <ContentHeader
      :title="isEditing ? formData.title : $t('tracks.create:header.title')"
      light-theme
      fixed
    >
      <Action
        slot="back"
        type="button"
        :text="$t('global:back.tracks')"
        class="btn-back"
        icon="keyboard_backspace"
        @click="leave()"
      />
      <ActionBar slot="actionbar" />
      <template slot="buttons">
        <Action
          :text="$t('global:form.save')"
          type="button"
          flat
          @click="submit(false)"
        />
      </template>
    </ContentHeader>
    <div class="main-content-inner">
      <div class="center">
        <div class="tracks-create-header">
          <h2 class="tracks-create-title">
            {{ $t('global:form.step', {'num': 3}) }}
          </h2>
          <p
            class="tracks-create-description"
            v-html="$t('tracks.create:header.description.3')"
          />
        </div>
        <form
          class="form"
          @submit.prevent="submit"
        >
          <InputField
            v-model="formData.name"
            :label="$t('global:form.tracks.title')"
            :validation="$v.formData.name"
            :counter="100"
          />
          <InputField
            v-model="formData.callText"
            :label="$t('global:form.tracks.callText')"
            :validation="$v.formData.callText"
            :counter="100"
          />
          <InputField
            v-model="formData.keywords"
            :label="$t('global:form.tracks.keywords')"
            :validation="$v.formData.keywords"
            :counter="100"
          />
        </form>
      </div>
    </div>
    <Pagination
      class="center"
      :active-first-last="false"
      :active-page="pagination.page"
      :page-count="pagination.lastPage"
      @nextPage="nextPage"
      @previousPage="prevPage"
    />
  </div>
</template>

<style src="@/assets/styles/themes/default/tracks.css"></style>
